/**
 * Derived client status — the single, truthful answer to "is this account a
 * client, and how do they pay?".
 *
 * The commercial truth is NOT `crm_accounts.status` (stale, polluted). It is the
 * revenue: a client pays either via an active Stripe subscription OR via a manual
 * invoice classified as a subscription (annual / semestrial / monthly). This
 * module derives that in one place so every surface (accounts page, account
 * sidebar, inbox CRM panel) shows the same thing instead of re-guessing.
 *
 * Two entry points, one vocabulary:
 *  - `clientBadgeFromMrr` : surfaces that already hold `mrrDetail` (CRM store).
 *  - `deriveClientBadge`  : surfaces without it (inbox), from raw DB signals.
 */

import { INVOICE_MRR_DIVISORS, type MRRClient } from './revenue';

/** Invoice revenue_category values that mean "paying subscription" (shared with revenue.ts). */
export const SUBSCRIPTION_INVOICE_CATEGORIES = Object.keys(INVOICE_MRR_DIVISORS);

export type ClientBillingSource = 'stripe' | 'invoice';
export type InvoiceCadence = 'annuelle' | 'semestrielle' | 'mensuelle';

export type ClientBadge =
  | { kind: 'client'; source: ClientBillingSource; cadence?: InvoiceCadence; detail?: string }
  | { kind: 'former' }
  | { kind: 'prospect' };

const CADENCE_BY_CATEGORY: Record<string, InvoiceCadence> = {
  subscription_annual: 'annuelle',
  subscription_semestrial: 'semestrielle',
  subscription_monthly: 'mensuelle',
};

export function cadenceFromCategory(category: string | null | undefined): InvoiceCadence | undefined {
  return category ? CADENCE_BY_CATEGORY[category] : undefined;
}

/**
 * Derive from raw signals. Used where `mrrDetail` isn't loaded (e.g. the inbox),
 * mirroring the source order of the MRR (SQL `get_mrr_breakdown`): active Stripe sub
 * wins, else a subscription invoice, else churned → former, else prospect.
 */
export function deriveClientBadge(input: {
  hasActiveStripeSub: boolean;
  subscriptionInvoiceCategory?: string | null;
  churned?: boolean;
}): ClientBadge {
  if (input.hasActiveStripeSub) return { kind: 'client', source: 'stripe' };
  if (input.subscriptionInvoiceCategory) {
    return { kind: 'client', source: 'invoice', cadence: cadenceFromCategory(input.subscriptionInvoiceCategory) };
  }
  if (input.churned) return { kind: 'former' };
  return { kind: 'prospect' };
}

/**
 * Derive from the canonical MRR client entry (`mrrDetail.clients`, keyed by
 * lead.id). `churned` is the fallback when the lead generates no active MRR.
 */
export function clientBadgeFromMrr(mrrClient: MRRClient | undefined, churned: boolean): ClientBadge {
  if (mrrClient && mrrClient.mrr > 0) {
    return { kind: 'client', source: mrrClient.source, detail: mrrClient.detail };
  }
  if (churned) return { kind: 'former' };
  return { kind: 'prospect' };
}

/**
 * Unified display shape for an account badge: derived client truth when the
 * account is (or was) a paying client, raw CRM status config otherwise. Shared
 * by the accounts list, its rows and the account record header.
 */
export interface AccountBadgeDisplay {
  label: string;
  color: string;
  dotColor: string;
}

/** Presentation for a badge — label + Tailwind classes, consistent everywhere. */
export function clientBadgeStyle(badge: ClientBadge): { label: string; color: string; dot: string } {
  switch (badge.kind) {
    case 'client': {
      const via =
        badge.source === 'stripe'
          ? 'Stripe'
          : badge.cadence
            ? `Facture ${badge.cadence}`
            : 'Facture';
      return { label: `Client · ${via}`, color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' };
    }
    case 'former':
      return { label: 'Ancien client', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' };
    case 'prospect':
      return { label: 'Prospect', color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' };
  }
}
